import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Edit2, Trash2, X, Check, AlertCircle, Loader2, Search, MapPin, Phone, RotateCcw, Save, Eye } from 'lucide-react';
import cabangService from '../services/cabang';
import DeleteConfirmModal from './DeleteConfirmModal';
import PaginationBar from './PaginationBar';
import usePagination from '../hooks/usePagination';

const MasterCabang = () => {
  const [cabangList, setCabangList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCabang, setEditingCabang] = useState(null);
  const [formData, setFormData] = useState({ name: '', alamat: '', kontak: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);

  // filteredCabang harus sebelum usePagination untuk hindari Temporal Dead Zone
  const filteredCabang = React.useMemo(() => {
    if (!cabangList.length) return [];
    return cabangList.filter(b =>
      !searchTerm || b.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [cabangList, searchTerm]);

  const { paginatedItems, currentPage, totalPages, totalItems, startIndex, endIndex, goToPage } = usePagination(filteredCabang, 10);

  const fetchCabang = useCallback(async () => {
    try {
      setLoading(true);
      const data = await cabangService.getAllCabang();
      setCabangList(data.cabang || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCabang(); }, [fetchCabang]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const resetForm = () => {
    setFormData({ name: '', alamat: '', kontak: '' });
    setFormError('');
    setEditingCabang(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) {
      setFormError('Nama cabang wajib diisi');
      return;
    }
    setFormLoading(true);
    try {
      if (editingCabang) {
        await cabangService.updateCabang(editingCabang._id, formData);
        showSuccess('Cabang updated successfully');
      } else {
        await cabangService.createCabang(formData);
        showSuccess('Cabang created successfully');
      }
      await fetchCabang();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm || isDeleting) return;
    setIsDeleting(true);
    try {
      await cabangService.deleteCabang(deleteConfirm._id);
      setDeleteConfirm(null);
      await fetchCabang();
      showSuccess('Cabang deleted successfully');
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Master Cabang</h2>
          <p className="mt-1 text-sm text-gray-500">Manage branches and office locations</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /><span>Add Cabang</span>
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm font-medium text-green-800">{successMessage}</p></div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Stats + Search */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 md:col-span-1">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Cabang</p>
              <p className="text-xl font-bold text-gray-900">{cabangList.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 md:col-span-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Search by nama cabang..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 hover:bg-white focus:bg-white" />
          </div>
        </div>
      </div>

      {/* Cabang List */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            <Building2 className="w-4 h-4 inline-block mr-1.5 text-amber-500" />
            Daftar Cabang
          </h3>
          <span className="text-xs text-gray-400">{filteredCabang.length} cabang</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nama Cabang</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Alamat</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Kontak</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto" /><p className="text-sm text-gray-500 mt-2">Loading...</p></td></tr>
              ) : paginatedItems.length > 0 ? (
                paginatedItems.map((b, idx) => (
                  <tr key={b._id} className="hover:bg-gray-50 transition-colors animate-slide-up" style={{ animationDelay: `${idx * 30}ms` }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{b.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {b.alamat ? (
                        <span className="flex items-center space-x-1"><MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" /><span>{b.alamat}</span></span>
                      ) : (
                        <span className="text-gray-300 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {b.kontak ? (
                        <span className="flex items-center space-x-1"><Phone className="w-3 h-3 text-gray-400 flex-shrink-0" /><span>{b.kontak}</span></span>
                      ) : (
                        <span className="text-gray-300 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {b.isActive !== false ? (
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button onClick={() => setViewDetail(b)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Lihat detail"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => { setEditingCabang(b); setFormData({ name: b.name, alamat: b.alamat || '', kontak: b.kontak || '' }); setShowModal(true); }}
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Edit"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteConfirm(b)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center">
                    <Building2 className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">No cabang found</p>
                    <p className="text-gray-400 text-sm mt-1">Click "Add Cabang" to create your first branch</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} goToPage={goToPage} />
      </div>

      {/* View Detail Modal */}
      {viewDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setViewDetail(null)}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Detail Cabang</h3>
                    <p className="text-sm text-gray-500">Informasi lengkap cabang</p>
                  </div>
                </div>
                <button onClick={() => setViewDetail(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-100">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{viewDetail.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full mt-1 ${
                      viewDetail.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${viewDetail.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`} />
                      {viewDetail.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Alamat</label>
                    <p className="text-sm font-semibold text-gray-900">{viewDetail.alamat || <span className="text-gray-400 italic">Tidak ada alamat</span>}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Kontak</label>
                    <p className="text-sm font-semibold text-gray-900">{viewDetail.kontak || <span className="text-gray-400 italic">Tidak ada kontak</span>}</p>
                  </div>
                  {viewDetail.createdAt && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Dibuat pada</label>
                      <p className="text-sm font-semibold text-gray-900">{new Date(viewDetail.createdAt).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <button onClick={() => setViewDetail(null)} className="btn-secondary text-sm">
                    <X className="w-4 h-4" /><span>Tutup</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Hapus Cabang"
        isLoading={isDeleting}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Nama Cabang</span>
          <span className="text-sm font-semibold text-gray-900">{deleteConfirm?.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Alamat</span>
          <span className="text-xs font-semibold text-gray-900 text-right max-w-[200px]">{deleteConfirm?.alamat || '-'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Kontak</span>
          <span className="text-xs font-semibold text-gray-900">{deleteConfirm?.kontak || '-'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Status</span>
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${deleteConfirm?.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${deleteConfirm?.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`} />
            {deleteConfirm?.isActive !== false ? 'Active' : 'Inactive'}
          </span>
        </div>
      </DeleteConfirmModal>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {editingCabang ? 'Edit Cabang' : 'Add New Cabang'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {editingCabang ? 'Update branch information' : 'Create a new branch or office location'}
                    </p>
                  </div>
                </div>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all" title="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}

                {/* Basic Information */}
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-5 h-5 text-amber-600" />
                      <h3 className="section-title">Basic Information</h3>
                    </div>
                  </div>
                  <div className="card-body space-y-5">
                    <div>
                      <label htmlFor="cabang-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Nama Cabang <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="cabang-name"
                        value={formData.name}
                        onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                        required
                        className="input-field"
                        placeholder="e.g. Cabang Utama"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact & Location */}
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-5 h-5 text-amber-600" />
                      <h3 className="section-title">Contact & Location</h3>
                    </div>
                  </div>
                  <div className="card-body space-y-5">
                    <div>
                      <label htmlFor="cabang-alamat" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Alamat
                      </label>
                      <textarea
                        id="cabang-alamat"
                        value={formData.alamat}
                        onChange={e => setFormData(p => ({ ...p, alamat: e.target.value }))}
                        rows={2}
                        className="input-field resize-none"
                        placeholder="Jl. Merdeka No. 1, Jakarta Pusat"
                      />
                    </div>
                    <div>
                      <label htmlFor="cabang-kontak" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Kontak
                      </label>
                      <input
                        type="text"
                        id="cabang-kontak"
                        value={formData.kontak}
                        onChange={e => setFormData(p => ({ ...p, kontak: e.target.value }))}
                        className="input-field"
                        placeholder="e.g. 021-1234567"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-between items-center pt-2">
                  <button type="button" onClick={resetForm} className="btn-secondary text-sm">
                    <RotateCcw className="w-4 h-4" />
                    <span>Reset</span>
                  </button>
                  <div className="flex space-x-3">
                    <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary text-sm">
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                    <button type="submit" disabled={formLoading} className="btn-primary text-sm">
                      {formLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                      ) : (
                        <><Save className="w-4 h-4" /><span>{editingCabang ? 'Update Cabang' : 'Save Cabang'}</span></>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterCabang;
